import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';
import { FileText, Copy, Plus, Filter, Tag, Zap, Smile, LayoutTemplate, X, BookOpen } from 'lucide-react';

const TemplateDetailModal = ({ template, details, onClose, onUse }) => {
  if (!template) return null;
  
  // Ensure details is an object
  const safeDetails = details || {};

  // Normalize structure/framework for display
  let steps = [];
  try {
    if (Array.isArray(safeDetails.structure)) {
        steps = safeDetails.structure;
    } else if (safeDetails.content_framework) {
        steps = safeDetails.content_framework.split('→');
    }
  } catch (e) {
    steps = [];
  }
  
  // Safe split for title template
  const titleParts = safeDetails.title_template ? safeDetails.title_template.split(/(\{.*?\})/) : [template.name];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{template.name}</h2>
            <div className="flex items-center space-x-2 mt-2">
              <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-xs font-medium rounded-full">
                {template.category}
              </span>
              {safeDetails.tone && (
                <span className="px-2 py-0.5 bg-purple-50 text-purple-600 text-xs font-medium rounded-full">
                  语气：{safeDetails.tone}
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto custom-scrollbar space-y-6">
          {/* Description */}
          <div className="bg-gray-50 p-4 rounded-xl text-gray-600 text-sm leading-relaxed">
            {safeDetails.description}
          </div>

          {/* Title Template */}
          {safeDetails.title_template && (
          <div>
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center">
              <Tag size={14} className="mr-2 text-blue-500" />
              标题公式
            </h3>
            <div className="bg-white border-2 border-blue-50 p-4 rounded-xl text-gray-800 font-medium text-base shadow-sm">
              {safeDetails.title_template.split(/(\{.*?\})/).map((part, i) => 
                part.startsWith('{') ? (
                  <span key={i} className="text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded mx-0.5 font-bold font-mono">
                    {part}
                  </span>
                ) : part
              )}
            </div>
          </div>
          )}

          {/* Content Framework */}
          {steps.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center">
                <BookOpen size={14} className="mr-2 text-purple-500" />
                内容框架详解
              </h3>
              <div className="space-y-3">
                {steps.map((step, i) => (
                  <div key={i} className="flex items-start p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <div className="w-6 h-6 rounded-full bg-white border border-gray-200 text-gray-500 flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0 shadow-sm mt-0.5">
                      {i+1}
                    </div>
                    <div className="text-sm text-gray-700 leading-relaxed">
                      {step.trim()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Suitable For */}
          {safeDetails.suitable_for && (
            <div>
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">适用场景</h3>
              <div className="flex flex-wrap gap-2">
                {safeDetails.suitable_for.map((tag, i) => (
                  <span key={i} className="px-3 py-1.5 bg-gray-100 text-gray-600 text-sm rounded-lg border border-gray-200">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-6 border-t border-gray-100 bg-gray-50/50 flex justify-end space-x-3">
          <button onClick={onClose} className="px-5 py-2.5 text-gray-600 font-medium hover:bg-gray-100 rounded-xl transition-colors">
            关闭
          </button>
          <button 
             onClick={() => onUse(template)}
             className="px-6 py-2.5 bg-blue-600 text-white font-bold rounded-xl shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all transform hover:-translate-y-0.5 flex items-center"
          >
            <Zap size={18} className="mr-2" />
            立即使用
          </button>
        </div>
      </div>
    </div>
  );
};

const TemplateCard = ({ template, onViewDetail, onUse }) => {
  let details = {};
  try {
    details = JSON.parse(template.content || '{}');
  } catch (e) {
    details = { description: template.content || '', title_template: template.name };
  }
  
  // Safe access
  const desc = details.description || '无描述';
  const steps = Array.isArray(details.structure) 
    ? details.structure 
    : (details.content_framework ? (details.content_framework.split ? details.content_framework.split('→') : []) : []);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300 group flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="p-5 pb-3">
        <div className="flex justify-between items-start mb-2">
          <div className="flex items-center space-x-2">
            <span className="p-2 bg-blue-50 text-blue-600 rounded-lg">
              <LayoutTemplate size={18} />
            </span>
            <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-500 rounded-full">
              {template.category || '通用'}
            </span>
          </div>
          {details.tone && (
            <span className="px-2 py-0.5 text-xs font-medium bg-purple-50 text-purple-600 rounded-full flex items-center">
              <Smile size={10} className="mr-1" />
              {details.tone.split('、')[0]}
            </span>
          )}
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors">
          {template.name}
        </h3>
        <p className="text-sm text-gray-500 line-clamp-2 min-h-[40px]">
          {desc}
        </p>
      </div>

      {/* Body: Structure & Preview */}
      <div className="px-5 py-3 bg-gray-50/50 flex-1 space-y-4 border-t border-b border-gray-100/50">
        
        {/* Title Formula */}
        {details.title_template && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1.5 flex items-center">
              标题公式
            </div>
            <div className="text-sm font-medium text-gray-800 bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
              {/* Highlight variables like {xxx} */}
              {details.title_template.split(/(\{.*?\})/).map((part, i) => 
                part.startsWith('{') ? (
                  <span key={i} className="text-blue-600 bg-blue-50 px-1 rounded mx-0.5 font-bold font-mono text-xs">
                    {part}
                  </span>
                ) : part
              )}
            </div>
          </div>
        )}

        {/* Content Framework Preview */}
        {steps.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1.5">
              内容框架
            </div>
            <div className="text-xs text-gray-600 space-y-1">
              {steps.slice(0, 3).map((step, i) => (
                <div key={i} className="flex items-center">
                  <span className="w-4 h-4 rounded-full bg-gray-200 text-gray-500 flex items-center justify-center text-[8px] mr-2 flex-shrink-0">
                    {i+1}
                  </span>
                  <span className="truncate">{step.trim()}</span>
                </div>
              ))}
              {steps.length > 3 && (
                <div className="pl-6 text-gray-400 text-[10px]">+ 更多步骤</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer: Tags & Action */}
      <div className="p-4 flex items-center justify-between mt-auto">
        <div className="flex gap-1 overflow-hidden">
          {details.suitable_for && details.suitable_for.slice(0, 2).map((tag, i) => (
            <span key={i} className="px-2 py-1 bg-gray-50 text-gray-500 text-[10px] rounded border border-gray-100 truncate max-w-[80px]">
              {tag}
            </span>
          ))}
        </div>
        <div className="flex space-x-2">
            <button 
                onClick={() => onViewDetail(template, details)}
                className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors"
            >
                详情
            </button>
            <button 
                onClick={() => onUse(template)}
                className="flex items-center space-x-1.5 text-xs font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition-colors"
            >
                <Zap size={14} />
                <span>使用</span>
            </button>
        </div>
      </div>
    </div>
  );
};

export default function Templates() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('ALL');
  const [viewMode, setViewMode] = useState('mine'); // 'mine' or 'all'
  const [selectedTemplate, setSelectedTemplate] = useState(null); // { template, details }

  useEffect(() => {
    if (user) fetchTemplates();
  }, [user, viewMode]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/templates?viewMode=${viewMode}`, {
        headers: { 'x-user-email': user.email }
      });
      setTemplates(res.data);
    } catch (err) {
      console.error("Failed to fetch templates", err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = (template, details) => {
    setSelectedTemplate({ template, details: details || {} });
  };
  
  const handleUseTemplate = (template) => {
    navigate('/new-project', { 
        state: { 
            templateId: template.id, 
            templateName: template.name 
        } 
    });
  };

  // Extract unique categories
  const categories = ['ALL', ...new Set(templates.map(t => t.category).filter(Boolean))];

  const filteredTemplates = activeCategory === 'ALL' 
    ? templates 
    : templates.filter(t => t.category === activeCategory);

  if (loading) return <div className="p-10 text-center text-gray-500">加载模板库...</div>;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header & Filter */}
      <header className="flex-shrink-0 bg-white border-b border-gray-200 px-8 py-5 flex items-center justify-between z-20">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">内容模板库</h1>
          <p className="text-gray-500 text-sm mt-1">
            基于 {user?.name || '您'} 的项目历史沉淀的 AI 写作方法论
          </p>
        </div>
        <div className="flex items-center space-x-4">
          {(user?.role === 'ADMIN' || user?.permissions?.includes('page:all')) && (
            <div className="flex bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setViewMode('mine')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'mine' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                我的模板
              </button>
              <button
                onClick={() => setViewMode('all')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'all' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                全部模板
              </button>
            </div>
          )}
          <button className="flex items-center space-x-2 bg-gray-900 text-white px-4 py-2 rounded-xl text-sm font-medium shadow-lg hover:bg-black transition-transform hover:-translate-y-0.5">
            <Plus size={16} />
            <span>新建模板</span>
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto custom-scrollbar p-8">
        {/* Category Tabs */}
        <div className="flex space-x-2 mb-8 overflow-x-auto pb-2">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                activeCategory === cat 
                ? 'bg-blue-600 text-white shadow-md shadow-blue-200' 
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              {cat === 'ALL' ? '全部主题' : cat}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredTemplates.map(template => (
            <TemplateCard 
                key={template.id} 
                template={template} 
                onViewDetail={handleViewDetail}
                onUse={handleUseTemplate}
            />
          ))}
          
          {filteredTemplates.length === 0 && (
            <div className="col-span-full py-20 text-center text-gray-400 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
              <Filter size={48} className="mx-auto mb-4 opacity-20" />
              <p>该主题下暂无模板</p>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {selectedTemplate && (
        <TemplateDetailModal 
            template={selectedTemplate.template} 
            details={selectedTemplate.details} 
            onClose={() => setSelectedTemplate(null)} 
            onUse={handleUseTemplate}
        />
      )}
    </div>
  );
}